import { FUEL, themeFor, type ChallengeInfo, type LeaderboardEntry } from '@accra/shared';
import type { PlayerVehicleId } from '../../content/playerVehicles';
import { UI_TEXT } from '../../content/lines/ui';
import { carriedFuel, type Profile } from '../../meta/profile';
import { currentStreak } from '../../meta/streak';
import { el, setText } from '../components/el';
import { Screen } from '../components/screen';
import { ChallengeBanner, DailyCard } from './challengeCards';
import { MissionsPanel } from './missionsPanel';
import { createRoutePicker } from './routePicker';
import { createVehiclePicker } from './vehiclePicker';
import { createTrafficPicker } from './trafficPicker';
import type { TrafficLevel } from '../../content/trafficLevels';

export interface MenuActions {
  start(): void;
  garage(): void;
  leaders(): void;
  pickVehicle(id: PlayerVehicleId): void;
  pickRoute(id: string): void;
  shuffleRoute(): void;
  playDaily(): void;
  me(): void;
  acceptChallenge(): void;
  dismissChallenge(): void;
  pickTraffic(level: TrafficLevel): void;
}

/** Title screen: today's theme, champion, route and trotro, missions, and the way into the garage and leaderboard. */
export class Menu extends Screen {
  private readonly picker: ReturnType<typeof createVehiclePicker>;
  private readonly routes: ReturnType<typeof createRoutePicker>;
  private readonly traffic: ReturnType<typeof createTrafficPicker>;
  private readonly daily: DailyCard;
  private readonly banner: ChallengeBanner;
  private readonly meButton: HTMLButtonElement;
  private readonly unseen = el('span', 'me-dot');
  private readonly missions = new MissionsPanel();
  private readonly today = el('div', 'menu-today');
  private readonly champion = el('div', 'menu-champion');
  private readonly best = el('div', 'menu-best');
  private readonly streak = el('div', 'streak-badge');

  constructor(parent: HTMLElement, actions: MenuActions) {
    super(parent, 'menu');
    this.picker = createVehiclePicker(actions.pickVehicle);
    this.routes = createRoutePicker(actions.pickRoute, actions.shuffleRoute);
    this.traffic = createTrafficPicker(actions.pickTraffic);
    this.daily = new DailyCard(actions.playDaily);
    this.banner = new ChallengeBanner(actions.acceptChallenge, actions.dismissChallenge);
    const title = el('h1', 'title');
    title.append(el('span', '', 'Trotro'), el('span', 'run', 'Run'), el('em', '', UI_TEXT.subtitle));
    this.meButton = this.button('👤 Me', actions.me);
    this.meButton.append(this.unseen);
    this.unseen.hidden = true;
    const buttons = el('div', 'menu-buttons');
    buttons.append(this.button(UI_TEXT.start, actions.start, 'primary big'), this.button('Garage', actions.garage), this.button('🏆 Leaders', actions.leaders), this.meButton);
    const steps = el('div', 'how-to-chips');
    // UI_TEXT.howTo.forEach((line, i) => {
    //   const chip = el('span', 'chip');
    //   chip.append(el('b', '', String(i + 1)), document.createTextNode(line));
    //   steps.append(chip);
    // });
    const keys = el('div', 'keys');
    UI_TEXT.controls.forEach(([key, action]) => {
      const row = el('span');
      row.append(el('kbd', '', key), document.createTextNode(` ${action}`));
      keys.append(row);
    });
    const touchHint = el('p', 'touch-hint', 'Swipe to change lane · swipe up to hop · hold GAS / BRAKE · tap 📯 to hoot');
    const stage = el('div', 'menu-stage');
    stage.append(title, el('div', 'flag-rule'), this.banner.root, this.today, this.champion, this.daily.root, this.routes.root, this.picker.root, this.traffic.root, buttons, steps, keys, touchHint, this.best);
    this.panel.replaceWith(stage);
    this.root.append(this.streak, this.missions.root);
  }

  showWith(profile: Profile): void {
    this.picker.select(profile.vehicle);
    this.routes.select(profile.routeId);
    this.traffic.select(profile.traffic);
    const theme = themeFor(Date.now());
    this.today.replaceChildren(el('span', '', `Today: ${theme.name}`), el('small', '', theme.blurb));
    const streak = currentStreak(profile.streak, Date.now());
    setText(this.streak, `🔥 ${streak}`);
    this.streak.title = `${streak}-day streak`;
    const tank = carriedFuel(profile.fuelUnits);
    const best = profile.runs > 0 ? `Best ${profile.bestScore.toLocaleString()}` : '';
    setText(this.best, tank < FUEL.tankUnits ? `${best}${best ? ' · ' : ''}⛽ ${Math.round(tank)}% in the tank` : best);
    this.missions.render(profile.missions, null, profile.walletPesewas);
    this.daily.refresh(Date.now());
    this.show();
  }

  /** Today's top three in the daily challenge. */
  setDailyBoard(rows: readonly LeaderboardEntry[] | null): void {
    this.daily.setBoard(rows);
  }

  /** Red number on the Me button: friends who tried your challenges since you last looked. */
  setUnseen(count: number): void {
    this.unseen.hidden = count <= 0;
    this.unseen.textContent = count > 9 ? '9+' : String(count);
  }

  /** A friend's challenge link, or null to hide it. */
  setChallenge(info: ChallengeInfo | null): void {
    this.banner.show(info);
  }

  setChampion(champion: LeaderboardEntry | null): void {
    this.champion.classList.toggle('show', champion !== null);
    if (!champion) return;
    this.champion.replaceChildren(el('span', '', "👑 Last week's champion"), el('b', '', champion.name));
  }
}
